import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { DebugUiBridge } from '../DebugUiBridge';
import { logger } from '../../core/Logger';

/**
 * Impact Effects Panel
 * 
 * Debug panel for testing screen impact effects (ripples, shakes).
 * Allows triggering presets and tweaking parameters in real-time.
 */
@customElement('impact-effects-panel')
export class ImpactEffectsPanel extends LitElement {
  @state() private activeEffects: number = 0;
  
  // Ripple parameters
  @property({ type: Number }) rippleCenterX = 0.5;
  @property({ type: Number }) rippleCenterY = 0.5;
  @property({ type: Number }) rippleAmplitude = 0.03;
  @property({ type: Number }) rippleDuration = 0.5;
  @property({ type: Number }) rippleFrequency = 10;
  @property({ type: Number }) rippleSpeed = 1.5;
  @property({ type: String }) rippleTimeRamp = 'explosion';
  
  // Shake parameters
  @property({ type: Number }) shakeAmplitude = 0.15;
  @property({ type: Number }) shakeDuration = 0.3;
  @property({ type: Number }) shakeFrequency = 30;
  @property({ type: Number }) shakeZoom = 1.03;
  @property({ type: String }) shakeTimeRamp = 'easeOut';
  
  static styles = css`
    :host {
      display: block;
      color: white;
      font-family: ui-sans-serif, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-size: 13px;
      padding: 16px;
    }
    
    .section {
      margin-bottom: 20px;
      padding-bottom: 16px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    }
    
    .section:last-of-type {
      border-bottom: none;
    }
    
    h3 {
      margin: 0 0 12px 0;
      font-size: 14px;
      font-weight: 600;
      opacity: 0.95;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    
    .status-badge {
      background: rgba(255, 255, 255, 0.1);
      padding: 2px 8px;
      border-radius: 10px;
      font-size: 11px;
      font-weight: normal;
    }
    
    .status-badge.active {
      background: rgba(76, 175, 80, 0.3);
      color: #81c784;
    }
    
    .preset-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 8px;
      margin-bottom: 16px;
    }
    
    button {
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 6px;
      color: white;
      padding: 10px 12px;
      cursor: pointer;
      font-size: 12px;
      font-weight: 500;
      transition: all 0.15s ease;
    }
    
    button:hover {
      background: rgba(255, 255, 255, 0.2);
      border-color: rgba(255, 255, 255, 0.3);
    }
    
    button:active {
      transform: scale(0.97);
    }
    
    button.primary {
      background: rgba(33, 150, 243, 0.3);
      border-color: rgba(33, 150, 243, 0.5);
    }
    
    button.primary:hover {
      background: rgba(33, 150, 243, 0.4);
    }
    
    .param-row {
      display: flex;
      align-items: center;
      gap: 12px;
      margin: 8px 0;
    }
    
    .param-row label {
      min-width: 80px;
      opacity: 0.75;
      font-size: 12px;
    }
    
    input[type="range"] {
      flex: 1;
      height: 20px;
      cursor: pointer;
    }
    
    .value {
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
      font-size: 11px;
      min-width: 40px;
      text-align: right;
      opacity: 0.9;
    }
    
    select {
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 4px;
      color: white;
      padding: 4px 8px;
      font-size: 12px;
      flex: 1;
      cursor: pointer;
    }
    
    select option {
      background: #2a2a2a;
      color: white;
    }
    
    .action-buttons {
      display: flex;
      gap: 8px;
      margin-top: 12px;
    }
    
    .action-buttons button {
      flex: 1;
    }
    
    .info {
      font-size: 11px;
      opacity: 0.6;
      margin-top: 8px;
      line-height: 1.4;
    }
  `;
  
  connectedCallback(): void {
    super.connectedCallback();
    // Could subscribe to effect state updates here
  }
  
  private triggerPreset(preset: string): void {
    DebugUiBridge.dispatchCommand({ type: 'impact:triggerPreset', preset });
    logger.ui.debug(`Triggered preset: ${preset}`);
  }
  
  private triggerCustomRipple(): void {
    DebugUiBridge.dispatchCommand({ 
      type: 'impact:triggerRipple', 
      config: {
        centerX: this.rippleCenterX,
        centerY: this.rippleCenterY,
        amplitude: this.rippleAmplitude,
        duration: this.rippleDuration,
        frequency: this.rippleFrequency,
        speed: this.rippleSpeed,
        timeRamp: this.rippleTimeRamp,
      }
    });
    logger.ui.debug(`Triggered custom ripple`);
  }
  
  private triggerCustomShake(): void {
    DebugUiBridge.dispatchCommand({ 
      type: 'impact:triggerShake', 
      config: {
        amplitude: this.shakeAmplitude,
        duration: this.shakeDuration,
        frequency: this.shakeFrequency,
        zoomAmount: this.shakeZoom,
        timeRamp: this.shakeTimeRamp,
      }
    });
    logger.ui.debug(`Triggered custom shake`);
  }
  
  private cancelAll(): void {
    DebugUiBridge.dispatchCommand({ type: 'impact:cancelAll' });
    logger.ui.debug(`Cancelled all effects`);
  }
  
  render() {
    return html`
      <div class="section">
        <h3>
          🎬 Presets
          <span class="status-badge ${this.activeEffects > 0 ? 'active' : ''}">
            ${this.activeEffects > 0 ? `${this.activeEffects} active` : 'idle'}
          </span>
        </h3>
        <div class="preset-grid">
          <button @click=${() => this.triggerPreset('smallImpact')}>💥 Small Impact</button>
          <button @click=${() => this.triggerPreset('mediumExplosion')}>💥 Medium Explosion</button>
          <button @click=${() => this.triggerPreset('largeExplosion')}>💥 Large Explosion</button>
          <button @click=${() => this.triggerPreset('powerUp')}>⭐ Power-Up</button>
          <button @click=${() => this.triggerPreset('groundPound')}>🦶 Ground Pound</button>
          <button @click=${() => this.triggerPreset('playerHit')}>💢 Player Hit</button>
          <button @click=${() => this.triggerPreset('criticalHit')}>⚡ Critical Hit</button>
          <button @click=${() => this.triggerPreset('earthquake')}>🌋 Earthquake</button>
        </div>
        <button @click=${this.cancelAll} style="width: 100%">❌ Cancel All Effects</button>
      </div>
      
      <div class="section">
        <h3>🌊 Ripple Effect</h3>
        
        <div class="param-row">
          <label>Center X</label>
          <input type="range" min="0" max="1" step="0.05" 
            .value=${String(this.rippleCenterX)}
            @input=${(e: Event) => this.rippleCenterX = Number((e.target as HTMLInputElement).value)}>
          <span class="value">${this.rippleCenterX.toFixed(2)}</span>
        </div>
        
        <div class="param-row">
          <label>Center Y</label>
          <input type="range" min="0" max="1" step="0.05"
            .value=${String(this.rippleCenterY)}
            @input=${(e: Event) => this.rippleCenterY = Number((e.target as HTMLInputElement).value)}>
          <span class="value">${this.rippleCenterY.toFixed(2)}</span>
        </div>
        
        <div class="param-row">
          <label>Amplitude</label>
          <input type="range" min="0.005" max="0.1" step="0.005"
            .value=${String(this.rippleAmplitude)}
            @input=${(e: Event) => this.rippleAmplitude = Number((e.target as HTMLInputElement).value)}>
          <span class="value">${this.rippleAmplitude.toFixed(3)}</span>
        </div>
        
        <div class="param-row">
          <label>Duration</label>
          <input type="range" min="0.1" max="2" step="0.1"
            .value=${String(this.rippleDuration)}
            @input=${(e: Event) => this.rippleDuration = Number((e.target as HTMLInputElement).value)}>
          <span class="value">${this.rippleDuration.toFixed(1)}s</span>
        </div>
        
        <div class="param-row">
          <label>Frequency</label>
          <input type="range" min="2" max="30" step="1"
            .value=${String(this.rippleFrequency)}
            @input=${(e: Event) => this.rippleFrequency = Number((e.target as HTMLInputElement).value)}>
          <span class="value">${this.rippleFrequency}</span>
        </div>
        
        <div class="param-row">
          <label>Speed</label>
          <input type="range" min="0.5" max="3" step="0.1"
            .value=${String(this.rippleSpeed)}
            @input=${(e: Event) => this.rippleSpeed = Number((e.target as HTMLInputElement).value)}>
          <span class="value">${this.rippleSpeed.toFixed(1)}</span>
        </div>
        
        <div class="param-row">
          <label>Time Ramp</label>
          <select .value=${this.rippleTimeRamp}
            @change=${(e: Event) => this.rippleTimeRamp = (e.target as HTMLSelectElement).value}>
            <option value="linear">Linear</option>
            <option value="easeOut">Ease Out</option>
            <option value="easeInOut">Ease In-Out</option>
            <option value="punch">Punch</option>
            <option value="bounce">Bounce</option>
            <option value="explosion">Explosion</option>
          </select>
        </div>
        
        <button class="primary" @click=${this.triggerCustomRipple} style="width: 100%; margin-top: 8px">
          🌊 Trigger Ripple
        </button>
      </div>
      
      <div class="section">
        <h3>📳 Screen Shake</h3>
        
        <div class="param-row">
          <label>Amplitude</label>
          <input type="range" min="0.01" max="0.5" step="0.01"
            .value=${String(this.shakeAmplitude)}
            @input=${(e: Event) => this.shakeAmplitude = Number((e.target as HTMLInputElement).value)}>
          <span class="value">${this.shakeAmplitude.toFixed(2)}</span>
        </div>
        
        <div class="param-row">
          <label>Duration</label>
          <input type="range" min="0.1" max="2" step="0.1"
            .value=${String(this.shakeDuration)}
            @input=${(e: Event) => this.shakeDuration = Number((e.target as HTMLInputElement).value)}>
          <span class="value">${this.shakeDuration.toFixed(1)}s</span>
        </div>
        
        <div class="param-row">
          <label>Frequency</label>
          <input type="range" min="5" max="60" step="1"
            .value=${String(this.shakeFrequency)}
            @input=${(e: Event) => this.shakeFrequency = Number((e.target as HTMLInputElement).value)}>
          <span class="value">${this.shakeFrequency} Hz</span>
        </div>
        
        <div class="param-row">
          <label>Zoom</label>
          <input type="range" min="1.0" max="1.2" step="0.01"
            .value=${String(this.shakeZoom)}
            @input=${(e: Event) => this.shakeZoom = Number((e.target as HTMLInputElement).value)}>
          <span class="value">${this.shakeZoom.toFixed(2)}x</span>
        </div>
        
        <div class="param-row">
          <label>Time Ramp</label>
          <select .value=${this.shakeTimeRamp}
            @change=${(e: Event) => this.shakeTimeRamp = (e.target as HTMLSelectElement).value}>
            <option value="linear">Linear</option>
            <option value="easeOut">Ease Out</option>
            <option value="easeInOut">Ease In-Out</option>
            <option value="punch">Punch</option>
            <option value="bounce">Bounce</option>
          </select>
        </div>
        
        <button class="primary" @click=${this.triggerCustomShake} style="width: 100%; margin-top: 8px">
          📳 Trigger Shake
        </button>
      </div>
      
      <div class="info">
        Effects are triggered via EventBus. Use presets for quick testing
        or customize individual parameters for fine-tuning.
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'impact-effects-panel': ImpactEffectsPanel;
  }
}
